package top.wcpe.allincore.dsl.ir.model.hud

import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import kotlin.jvm.JvmField
import kotlin.jvm.JvmOverloads
import kotlin.jvm.JvmStatic

/** HUD 顶层元数据聚合对象，避免 UiIr 继续平铺 HUD-only 字段。 */
class UiHudMeta @JvmOverloads constructor(
    // val 属性生成 getAnchor() / getLayer()，与原 Java getter 名一致。
    val anchor: String?,
    val layer: String?,
    zIndex: Int,
    hide: List<String>?,
    layoutPolicy: HudLayoutPolicy?,
    stackPolicy: HudStackPolicy?,
    visibilityPolicy: HudVisibilityPolicy?,
    tickBodyBytes: ByteArray?,
    keyBindings: List<UiKeyBinding>? = emptyList(),
) {

    // 字段 zIndex 生成 getZIndex()，与原 Java 一致。
    val zIndex: Int = zIndex
    val hide: List<String> = copyHide(hide)
    val layoutPolicy: HudLayoutPolicy = layoutPolicy ?: HudLayoutPolicy.DEFAULT
    val stackPolicy: HudStackPolicy = stackPolicy ?: HudStackPolicy.DEFAULT
    val visibilityPolicy: HudVisibilityPolicy = visibilityPolicy ?: HudVisibilityPolicy.DEFAULT

    // 防御式拷贝：入参数组构造时复制。
    private val tickBodyBytesInternal: ByteArray? = tickBodyBytes?.copyOf()

    // 属性 + 自定义 getter：Java 侧仍见 getTickBodyBytes() 返回防御式拷贝，Kotlin 侧以 .tickBodyBytes 访问。
    val tickBodyBytes: ByteArray?
        get() = tickBodyBytesInternal?.copyOf()

    /** Phase 4：HUD keybinding 按键绑定声明列表。 */
    val keyBindings: List<UiKeyBinding> =
        if (keyBindings == null) emptyList() else ReadOnlyList(ArrayList(keyBindings))

    fun hasAnyField(): Boolean {
        return anchor != null ||
            layer != null ||
            zIndex != 0 ||
            hide.isNotEmpty() ||
            HudLayoutPolicy.DEFAULT != layoutPolicy ||
            HudStackPolicy.DEFAULT != stackPolicy ||
            HudVisibilityPolicy.DEFAULT != visibilityPolicy ||
            tickBodyBytesInternal != null ||
            keyBindings.isNotEmpty()
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiHudMeta) return false
        return zIndex == other.zIndex &&
            anchor == other.anchor &&
            layer == other.layer &&
            hide == other.hide &&
            layoutPolicy == other.layoutPolicy &&
            stackPolicy == other.stackPolicy &&
            visibilityPolicy == other.visibilityPolicy &&
            tickBodyBytesInternal.contentEquals(other.tickBodyBytesInternal) &&
            keyBindings == other.keyBindings
    }

    // 逐位复刻：Objects.hash(anchor, layer, zIndex, hide, layoutPolicy, stackPolicy, visibilityPolicy, keyBindings)
    // （种子 1、31 倍累乘），再 31*result + Arrays.hashCode(tickBodyBytes)。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + (anchor?.hashCode() ?: 0)
        result = 31 * result + (layer?.hashCode() ?: 0)
        result = 31 * result + zIndex
        result = 31 * result + hide.hashCode()
        result = 31 * result + layoutPolicy.hashCode()
        result = 31 * result + stackPolicy.hashCode()
        result = 31 * result + visibilityPolicy.hashCode()
        result = 31 * result + keyBindings.hashCode()
        result = 31 * result + tickBodyBytesInternal.contentHashCode()
        return result
    }

    override fun toString(): String =
        "UiHudMeta{anchor='" + anchor + "', layer='" + layer + "', zIndex=" + zIndex +
            ", hide=" + hide + ", layout=" + layoutPolicy + ", stack=" + stackPolicy +
            ", visibility=" + visibilityPolicy + ", tickBodyBytes=" +
            (if (tickBodyBytesInternal == null) "null" else tickBodyBytesInternal.size.toString() + "B") +
            ", keyBindings=" + keyBindings.size + "}"

    companion object {
        // @JvmField 使 Java 侧仍以 UiHudMeta.EMPTY 静态字段访问。
        @JvmField
        val EMPTY = UiHudMeta(
            null,
            null,
            0,
            emptyList(),
            HudLayoutPolicy.DEFAULT,
            HudStackPolicy.DEFAULT,
            HudVisibilityPolicy.DEFAULT,
            null,
            emptyList(),
        )

        @JvmStatic
        fun legacy(anchor: String?, layer: String?, hide: List<String>?): UiHudMeta =
            UiHudMeta(
                anchor, layer, 0, hide,
                HudLayoutPolicy.DEFAULT,
                HudStackPolicy.DEFAULT,
                HudVisibilityPolicy.DEFAULT,
                null,
            )

        // 防御式拷贝：入参为空返回空列表，逐元素非空校验，保序。
        private fun copyHide(hide: List<String>?): List<String> {
            if (hide.isNullOrEmpty()) {
                return emptyList()
            }
            val copied = ArrayList<String>(hide.size)
            for (token in hide) {
                copied.add(requireNotNull(token) { "hudHide token" })
            }
            return ReadOnlyList(copied)
        }
    }
}
